/**
 * /knowledge — user-mode view of prompts assigned to me.
 *
 * Per D-146 IA principle + Push #3.2 pillar parity: tile-grid clickable rows
 * mirroring /connectors UX. Click any tile → /knowledge/:slug (template name
 * for catalog-derived rows, instance UUID for custom prompts).
 *
 * Data source: /api/me/knowledge (RLS Pattern 3 visibility filter).
 *
 * Drill-down: /knowledge/:slug renders KnowledgeDetail with Lightning record
 * page (tabs: Overview | Body | Variables | Permission sets).
 *
 * Push #4 (2026-06-25): extracted into `./knowledge/` sub-modules along the
 * natural seams (CreatePromptForm + MyPromptsSection + AvailablePromptsSection
 * + KnowledgeSkeleton + the shared VISIBILITY_LABEL). This file is now an
 * orchestrator: it owns fetch + RequestModal state + the header CTA panel.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useSession } from '../hooks/useSession.tsx';
import {
  fetchMyKnowledgePrompts,
  fetchMyKnowledgeCatalog,
  ApiError,
  type KnowledgePromptInstance,
  type MyKnowledgeCatalogRow,
} from '../lib/api.ts';
import { ObjectIcon } from '../components/ObjectIcon.tsx';
import { RequestModal } from '../components/RequestModal.tsx';

import { CreatePromptForm } from './knowledge/CreatePromptForm.tsx';
import { MyPromptsSection } from './knowledge/MyPromptsSection.tsx';
import { AvailablePromptsSection } from './knowledge/AvailablePromptsSection.tsx';
import { KnowledgeSkeleton } from './knowledge/KnowledgeSkeleton.tsx';

export function Knowledge() {
  const { user } = useSession();
  const [list, setList] = useState<KnowledgePromptInstance[] | null>(null);
  const [catalog, setCatalog] = useState<MyKnowledgeCatalogRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requestSlug, setRequestSlug] = useState<string | null>(null);

  // Per Ean 2026-06-10 sharing-model refactor: creation is ALWAYS private to
  // the author. After save, the user can hit Share on the prompt's record
  // page to either (a) submit for admin review for workspace-wide distribution
  // or (b) share with specific teammates directly. This drops the binary
  // visibility picker at creation in favor of an explicit post-creation
  // sharing flow.
  const [showCreateForm, setShowCreateForm] = useState(false);

  async function reload() {
    try {
      const [mine, cat] = await Promise.all([
        fetchMyKnowledgePrompts(),
        fetchMyKnowledgeCatalog(),
      ]);
      setList(mine.prompts);
      setCatalog(cat.templates);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load knowledge.');
    }
  }

  // Three-section split per Ean 2026-06-10: yours (instance rows from
  // /api/me/knowledge), available (template installed in workspace but not
  // assigned to me — derived from catalog with is_installed && !is_mine),
  // coming soon (template is_preview=true). Mirrors /connectors pattern.
  // D-156 catalog IA: 3-bucket projection. The "comingSoon" key here is the
  // Public catalog bucket (states 3 + 4) — variable kept for diff-minimal
  // refactor. State 3 = active templates not installed on this account
  // (chip="Available", action="Request install"). State 4 = is_preview=true
  // roadmap teasers (chip="Coming soon", action="Notify me").
  const catalogSections = useMemo(() => {
    if (catalog === null) return { available: [], comingSoon: [] };
    const available: MyKnowledgeCatalogRow[] = [];
    const publicCatalog: MyKnowledgeCatalogRow[] = [];
    for (const t of catalog) {
      if (t.is_mine) continue; // user-mode pages render is_mine rows from the instance list, not catalog
      if (t.is_installed) {
        // State 2: instance exists on workspace, not assigned to me.
        available.push(t);
      } else {
        // State 3 (active uninstalled) OR State 4 (preview/coming_soon).
        publicCatalog.push(t);
      }
    }
    return { available, comingSoon: publicCatalog };
  }, [catalog]);

  useEffect(() => {
    void reload();
  }, []);

  if (user === null) return null;

  return (
    <div>
      <div className="lightning-highlight">
        <div className="lightning-highlight-left">
          <ObjectIcon kind="prompts" size="lg" />
          <div className="lightning-highlight-text">
            <h1 className="lightning-title">My prompts</h1>
            <p
              className="muted"
              style={{ margin: '6px 0 0', fontSize: '13px', maxWidth: '72ch', lineHeight: 1.45 }}
            >
              Reusable prompt templates to use in Olytic chat, or through Claude Desktop, ChatGPT, or any MCP-aware AI client. Click any tile to see the body, variables, and how you have access.
            </p>
          </div>
        </div>
        {/* Action ordering per Ean 2026-06-09: UNIQUE actions (specific to this
           pillar) sit LEFT; FORMULAIC actions (present on every pillar page)
           sit RIGHT. Create my own is unique to Prompts (Connectors + Models
           don't have an inline create surface yet). Browse market + Go to
           chat are universal across every pillar page, so they cluster on
           the right. Keeps the cross-page button rhythm consistent. */}
        {/* Push 4-1c-e (2026-07-01): Prompts are Group 2 BYO — no Market
            surface (removed in Push 4-1b Market surgery). "Create your
            own" is the PRIMARY action; Market link retired. */}
        <div className="lightning-highlight-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowCreateForm((v) => !v)}
          >
            {showCreateForm ? 'Cancel' : 'Create your own'}
          </button>
          <Link to="/chat" className="btn btn-secondary">
            Go to chat
          </Link>
        </div>
      </div>

      {showCreateForm && (
        <CreatePromptForm
          onCreated={async () => {
            setShowCreateForm(false);
            await reload();
          }}
        />
      )}

      {error !== null && (
        <div className="callout callout-danger" style={{ marginBottom: '16px' }}>
          <p className="callout-body">{error}</p>
        </div>
      )}

      {list === null && error === null && <KnowledgeSkeleton />}

      {/* D-159 + Ean 2026-06-10 acceptance criteria: two always-visible sections.
         Section A "What you can use" includes prompts assigned to me by an admin
         (visibility = 'account_installed') AND prompts I've created myself
         (visibility = 'user_private' / 'user_shared' / 'account_custom'). both
         are assigned, just tagged differently via the VISIBILITY_LABEL pill.
         Section B "What you could use" = templates installed in workspace but
         not assigned to me yet. Both sections render even when empty so the
         shape stays predictable. */}
      {list !== null && (
        <>
          <MyPromptsSection
            list={list}
            onCreate={() => setShowCreateForm(true)}
          />
          <AvailablePromptsSection
            available={catalogSections.available}
            onRequest={(slug) => setRequestSlug(slug)}
          />
        </>
      )}

      {/* Public catalog. states 3 + 4 with chip + action differentiation
          per D-156. State 3 (active uninstalled, chip="Available") shows
          Request install. State 4 (is_preview=true, chip="Coming soon")
          shows Notify me / vote. */}
      {/* D-159: Public catalog (states 3+4) moved to /catalog. The Catalog
         top-level nav is the marketplace; pillar pages stay workspace-scoped. */}

      {/* Trust whisper. clarifies that prompts are scaffolds shaped for
         your role, not free-form templates. Reinforces the value-prop. */}
      {list !== null && list.length > 0 && (
        <div className="trust-whisper trust-whisper--footer">
          Prompts here are shaped by your admin for the work your role does · Ones you create stay yours or can be shared
        </div>
      )}

      <RequestModal
        open={requestSlug !== null}
        onClose={() => setRequestSlug(null)}
        destination="admin"
        initialKind="feedback"
        {...(requestSlug !== null ? { subjectHint: `Prompt request: ${requestSlug}` } : {})}
      />
    </div>
  );
}
